/**
 * G-FILE: one in-flight GET per revision-file URL, plus a 404 cache.
 *
 * Official Fetch (`credentials: "include"`). Callers import
 * `fetchRevisionFileOnce` — no globalThis.fetch wrap.
 * https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API
 *
 * In-flight Map is only for overlap. On settle the entry is deleted and the
 * body is already consumed into ArrayBuffer/text (never an unconsumed
 * Response). A later success caller issues a new GET (or the browser HTTP
 * cache: Worker `private, max-age=60`).
 *
 * 404 cache: persistRevisionFromBundle copies the stable git bundle in R2
 * first (`persist-revision.ts` `copyR2Object` ~73), then INSERT
 * hardware_revisions (~90–103) and refuses the INSERT if the copy failed.
 * Job tree/zip objects are written before complete. Once a rev row is
 * visible its git_oid + parse prefix file set does not grow. A 404 for
 * `/revisions/:revId/files/:path` cannot later become 200 for that revId.
 * Not a TTL. No 404 retry.
 */

const REVISION_FILE_PATH = /\/hardware\/revisions\/[^/]+\/files\//;

type FlightBody = {
  status: number;
  body: ArrayBuffer | string;
  contentType: string;
  cacheControl: string | null;
};

const inFlight = new Map<string, Promise<FlightBody>>();
const notFound = new Map<string, string>();

/** One-slot handoff: the current prefetch GET, consumed by inspect. Not a cache. */
let prefetchHandoff: { key: string; promise: Promise<Response> } | null = null;

export function isRevisionFileHref(href: string): boolean {
  return revisionFileFetchKey(href) != null;
}

/** Path+search so `/api/…` and `http://origin/api/…` share one slot. */
export function revisionFileFetchKey(href: string): string | null {
  if (!href) return null;
  try {
    const url = new URL(href, "https://pcbx.ai");
    if (!REVISION_FILE_PATH.test(url.pathname)) return null;
    return `${url.pathname}${url.search}`;
  } catch {
    return REVISION_FILE_PATH.test(href) ? href : null;
  }
}

export function revisionFileInFlightCount(): number {
  return inFlight.size;
}

function notFoundBody(body: string): FlightBody {
  return {
    status: 404,
    body,
    contentType: "application/json",
    cacheControl: "no-store",
  };
}

function toResponse(settled: FlightBody): Response {
  const headers = new Headers();
  if (settled.contentType) headers.set("content-type", settled.contentType);
  if (settled.cacheControl) headers.set("cache-control", settled.cacheControl);
  return new Response(settled.body, { status: settled.status, headers });
}

function abortReason(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException("The operation was aborted.", "AbortError");
}

/** Reject only this caller when its signal aborts. Shared GET has no signal. */
function followCallerSignal<T>(pending: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return pending;
  if (signal.aborted) return Promise.reject(abortReason(signal));
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => {
      reject(abortReason(signal));
    };
    signal.addEventListener("abort", onAbort, { once: true });
    pending.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (err) => {
        signal.removeEventListener("abort", onAbort);
        reject(err);
      },
    );
  });
}

/**
 * Shared revision-file GET while in flight. Each caller gets a new Response
 * built from the consumed body (text/ArrayBuffer).
 */
export async function fetchRevisionFileOnce(
  href: string,
  fetchImpl: typeof fetch = fetch,
  signal?: AbortSignal,
): Promise<Response> {
  if (signal?.aborted) throw abortReason(signal);
  const key = revisionFileFetchKey(href);
  if (!key) return fetchImpl(href, { credentials: "include" });
  const cached = notFound.get(key);
  if (cached != null) return toResponse(notFoundBody(cached));

  let pending = inFlight.get(key);
  if (!pending) {
    pending = (async () => {
      const res = await fetchImpl(href, { credentials: "include" });
      if (res.status === 404) {
        const body = await res.text();
        notFound.set(key, body);
        return notFoundBody(body);
      }
      const body = await res.arrayBuffer();
      return {
        status: res.status,
        body,
        contentType: res.headers.get("content-type") ?? "",
        cacheControl: res.headers.get("cache-control"),
      };
    })();
    inFlight.set(key, pending);
    void pending.finally(() => {
      if (inFlight.get(key) === pending) inFlight.delete(key);
    });
  }

  const settled = await followCallerSignal(pending, signal);
  return toResponse(settled);
}

/** Record the prefetch Promise so inspect can consume this same GET. */
export function rememberRevisionFilePrefetch(href: string, promise: Promise<Response>): void {
  const key = revisionFileFetchKey(href);
  if (!key) return;
  prefetchHandoff = { key, promise };
}

/**
 * Peek the current prefetch Promise for this href. Does not clear — StrictMode
 * remount / a second inspect must reuse this GET, not start another.
 * `fetchRevisionFileOnce` after settle still issues a new GET (not a cache).
 * Each caller gets `Response.clone()` so the stored body stays unread.
 */
export function consumeRevisionFilePrefetch(
  href: string,
  signal?: AbortSignal,
): Promise<Response> | null {
  const key = revisionFileFetchKey(href);
  if (!key || !prefetchHandoff || prefetchHandoff.key !== key) return null;
  return followCallerSignal(
    prefetchHandoff.promise.then((res) => res.clone()),
    signal,
  );
}

export function resetRevisionFileFetches(): void {
  inFlight.clear();
  notFound.clear();
  prefetchHandoff = null;
}
